import { SHARED, INCOME_DISCLAIMER } from 'app/models/questions/shared';
import { Currency } from 'app/components/FormFields/Currency';
import { IntegerAllowZero } from 'app/components/FormFields/IntegerAllowZero';
import { Name } from 'app/components/FormFields/Name';
import { RadioField } from 'app/components/FormFields/RadioField';
import { Select } from 'app/components/FormFields/Select';
import { SinglePastDate } from 'app/components/FormFields/SinglePastDate';
import { Address } from 'app/components/FormFields/Address';
import { ZipCode } from 'app/components/FormFields/ZipCode';
import { TextInput } from 'app/components/FormFields/TextInput';
import { State } from 'app/components/FormFields/State';
import {
  EMPLOYMENT_TYPE_OPTIONS,
  REFINANCE_REASON_OPTIONS,
  YES_OR_NO_OPTIONS,
  LIVING_SITUATION_OPTIONS,
  COBORROWER_EMPLOYMENT_TYPE_OPTIONS,
  ACCOUNT_TYPE_REFINANCE_OPTIONS,
  LoanTypeOptions,
} from 'app/models/options/options';
import { AdditionalProperties } from 'app/components/FormFieldArrays/AdditionalProperties';
import { BankAccounts } from 'app/components/FormFieldArrays/BankAccounts';
import { IncomeSources } from 'app/components/FormFieldArrays/IncomeSources';
import { RetirementIncomeSources } from 'app/components/FormFieldArrays/RetirementIncomeSources';
import { OtherIncomeSources } from 'app/components/FormFieldArrays/OtherIncomeSources';
import { Question } from 'app/models/types';
import { FieldNames } from 'app/models/fields/names';
import {
  hasAddlProperties,
  hasCoBorrower,
  isEmployee,
  isSelfEmployed,
  isLessThanTwoYrsAtJob,
  isRetired,
  isEmploymentOther,
  isLessThanTwoYrsAtLivingAddress,
  isSecondaryHome,
  isInvestmentHome,
  coBorrowerHasDifferentAddress,
  isCoBorrowerRenting,
  isCoLessThanTwoYrsAtJob,
  isCoBorrowerRetired,
  isCoBorrowerEmploymentOther,
  hasShowNumberOfDependents,
  hasShowAgesOfDependents,
  isMailingDifferent,
  isCollectingMailingAddress,
  hasLineOfWorkQuestion,
} from 'app/models/fields/conditionals';
import { Year } from 'app/components/FormFields/Year';
import { CollectConditionallyRequiredSsn } from 'app/components/FormFields/Ssn/CollectConditionallyRequiredSsn';
import { AgesFFAComponent } from 'app/components/FormFieldArrays/Dynamic/Ages';
import { IntegerAllowBlank } from 'app/components/FormFields/IntegerAllowBlank';

/**
 * Takes in a field name and returns the corresponding purchase
 * question
 *
 * @param {string} name Field name
 * @returns {Question} Question with matching field name
 */
export const refinanceLFQuestionByName = (name: string): Question => {
  return APPLICATION_REFINANCE_QUESTIONS.find((question) => {
    return question.name === name;
  }) as Question;
};

/**
 * Takes in an array of field names and returns an array of questions
 *
 * @param {string[]} [names=[]] Field names
 * @returns Questions matching field names
 */
export const refinanceLFQuestionsByName = (names: string[] = []): Question[] => {
  const questions: Question[] = [];
  for (const name of names) {
    questions.push(refinanceLFQuestionByName(name));
  }
  return questions;
};

export const APPLICATION_REFINANCE_QUESTIONS: Question[] = [
  {
    id        : 1001,
    component : RadioField,
    label     : 'Purpose of Refinance',
    hideLabel : true,
    name      : FieldNames.loanPurpose,
    options   : REFINANCE_REASON_OPTIONS,
    title     : 'What is the purpose of your refinance?',
  },
  {
    id: 1002,
    ...SHARED[FieldNames.firstName],
  },
  {
    id: 1003,
    ...SHARED[FieldNames.middleName],
  },
  {
    id: 1004,
    ...SHARED[FieldNames.lastName],
  },
  {
    id       : 1005,
    ...SHARED[FieldNames.dateOfBirth],
  },
  {
    id: 1006,
    ...SHARED[FieldNames.email],
  },
  {
    id: 1007,
    ...SHARED[FieldNames.phone],
  },
  {
    id       : 1008,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.propertyStreet,
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet,
    },
  },
  {
    id        : 1009,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.propertyStreet2,
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.propertyStreet2,
    },
  },
  {
    id       : 1010,
    component: Address,
    label    : 'City',
    name     : FieldNames.propertyCity,
  },
  {
    id       : 1011,
    component: State,
    label    : 'State',
    name     : FieldNames.propertyState,
  },
  {
    id       : 1012,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.propertyZip,
  },
  {
    id: 1013,
    ...SHARED[FieldNames.propertyType],
    component   : Select,
    fieldPopover: true,
  },
  {
    id: 1014,
    ...SHARED[FieldNames.propertyUsage],
    component   : Select,
    fieldPopover: true,
  },
  {
    id       : 1015,
    component: Currency,
    label    : 'Estimated Home Value',
    name     : FieldNames.homeValue,
    validator: {
      type : 'isGreaterThanField',
      param: FieldNames.loanAmount,
    },
  },
  {
    id       : 1016,
    component: Currency,
    label    : 'Loan amount you would like to refinance',
    name     : FieldNames.loanAmount,
    validator: {
      type : 'loanAmountIsValid',
      param: FieldNames.homeValue,
    },
  },
  {
    ...SHARED[FieldNames.hasAddlPropertiesYN],
    id    : 1017,
    title : 'Do you own additional properties?',
  },
  {
    id       : 1018,
    component: AdditionalProperties,
    name     : FieldNames.addlProperties,
    showIf   : [hasAddlProperties],
  },
  {
    id: 1019,
    ...SHARED[FieldNames.creditScore],
    title: 'What is your estimated credit score?',
  },
  {
    id: 1020,
    ...SHARED[FieldNames.maritalStatus],
  },
  {
    id       : 1021,
    component: IntegerAllowZero,
    name     : FieldNames.numOfDependent,
    title    : 'Number of dependents',
    showIf   : [hasShowNumberOfDependents],
  },
  {
    id       : 1022,
    component: AgesFFAComponent,
    name     : FieldNames.dependentsAges,
    title    : 'Do you have any Dependents?',
    showIf   : [hasShowAgesOfDependents],
  },
  {
    id: 1023,
    component: RadioField,
    name     : FieldNames.coBorrowerYN,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Will you have a co-borrower?',
  },
  {
    id: 1024,
    ...SHARED[FieldNames.firstName],
    label : 'Co-Borrower\'s First Name',
    name  : FieldNames.coBorrowerFirstName,
    showIf: [hasCoBorrower],
  },
  {
    id        : 1025,
    component : Name,
    label     : 'Co-Borrower\'s Middle Name',
    name      : FieldNames.coBorrowerMiddleName,
    isOptional: true,
    showIf    : [hasCoBorrower],
  },
  {
    id: 1026,
    ...SHARED[FieldNames.lastName],
    label : 'Co-Borrower\'s Last Name',
    name  : FieldNames.coBorrowerLastName,
    showIf: [hasCoBorrower],
  },
  {
    id: 1027,
    ...SHARED[FieldNames.dateOfBirth],
    label : 'Co-Borrower\'s Date of Birth',
    name  : FieldNames.coBorrowerDOB,
    showIf: [hasCoBorrower],
  },
  {
    id       : 1028,
    component: RadioField,
    name     : FieldNames.coBorrowerOnTitle,
    options  : YES_OR_NO_OPTIONS,
    title    : 'Will they be on the title?',
    showIf   : [hasCoBorrower],
  },
  {
    id: 1029,
    ...SHARED[FieldNames.escrowUsage],
    name : FieldNames.livingEscrowUsage,
    title: 'Do you have an escrow account?',
  },
  {
    id: 1030,
    ...SHARED[FieldNames.escrowUsage],
    options: YES_OR_NO_OPTIONS,
    title  : 'Will you have an escrow with your new loan?',
  },
  {
    id        : 1031,
    component : Currency,
    label     : 'Annual Taxes',
    name      : FieldNames.livingYearTaxes,
    title     : 'Current taxes, insurance, and fees',
    showIf    : [isSecondaryHome, isInvestmentHome],
  },
  {
    id        : 1032,
    component : Currency,
    label     : 'Annual Insurance',
    name      : FieldNames.livingYearInsure,
    showIf    : [isSecondaryHome, isInvestmentHome],
  },
  {
    id         : 1033,
    component  : Currency,
    isOptional : true,
    label      : 'Annual Association Fees',
    name       : FieldNames.livingYearAssociate,
    showIf     : [isSecondaryHome, isInvestmentHome],
  },
  {
    id         : 1034,
    component  : RadioField,
    name       : FieldNames.employmentType,
    options    : EMPLOYMENT_TYPE_OPTIONS,
    disclaimer : INCOME_DISCLAIMER,
  },
  {
    id       : 1035,
    component: TextInput,
    label    : 'Company Name',
    name     : FieldNames.employCompanyName,
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id       : 1036,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.employStreet,
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id        : 1037,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.employStreet2,
    showIf    : [isEmployee, isSelfEmployed],
  },
  {
    id       : 1038,
    component: Address,
    label    : 'City',
    name     : FieldNames.employCity,
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id       : 1039,
    component: State,
    label    : 'State',
    name     : FieldNames.employState,
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id       : 1040,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.employZip,
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id       : 1041,
    component: SinglePastDate,
    label    : 'Start Date',
    name     : FieldNames.employStart,
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id       : 1042,
    component: Currency,
    label    : 'Monthly Base Pay',
    name     : FieldNames.employBase,
    showIf   : [isEmployee, isSelfEmployed],
  },
  {
    id          : 1043,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Overtime',
    name        : FieldNames.employOvertime,
    showIf      : [isEmployee, isSelfEmployed],
    popover     : 'Overtime can be included in your income if it has been consistent over a two-year period.',
  },
  {
    id          : 1044,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Commission',
    name        : FieldNames.employCommission,
    showIf      : [isEmployee, isSelfEmployed],
    popover     : 'Commissions can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id          : 1045,
    component   : Currency,
    fieldPopover: true,
    isOptional  : true,
    label       : 'Monthly Bonus',
    name        : FieldNames.employBonus,
    showIf      : [isEmployee, isSelfEmployed],
    popover     : 'Bonuses can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id       : 1046,
    component: TextInput,
    label    : 'Previous Employers Name',
    name     : FieldNames.employPrevName,
    title    : 'Previous employment information',
    showIf   : [isLessThanTwoYrsAtJob],
    popover  : 'Lenders will require previous employer information if you\'ve been with this employer for less than 2 years.',
  },
  {
    id: 1047,
    component: Address,
    label    : 'Street Address',
    name     : FieldNames.employPrevStreet,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id        : 1048,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.employPrevStreet2,
    showIf    : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1049,
    component: Address,
    label    : 'City',
    name     : FieldNames.employPrevCity,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1050,
    component: State,
    label    : 'State',
    name     : FieldNames.employPrevState,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1051,
    component: ZipCode,
    label    : 'Zip',
    name     : FieldNames.employPrevZip,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1052,
    component: SinglePastDate,
    label    : 'Start Date',
    name     : FieldNames.employPrevStart,
    showIf   : [isLessThanTwoYrsAtJob],
  },
  {
    id       : 1053,
    component: SinglePastDate,
    label    : 'End Date',
    name     : FieldNames.employPrevEnd,
    showIf   : [isLessThanTwoYrsAtJob],
    validator: {
      type : 'isAfterStartDate',
      param: [FieldNames.employPrevStart, FieldNames.employStart],
    },
  },
  {
    id        : 1054,
    component : IncomeSources,
    title     : 'Additional income sources?',
    name      : FieldNames.additionalIncome,
  },
  {
    id       : 1055,
    component: RetirementIncomeSources,
    title    : 'Tell us about your retirement income',
    name     : FieldNames.retirementIncome,
    showIf   : [isRetired],
  },
  {
    id       : 1056,
    component: OtherIncomeSources,
    title    : 'Tell us about your other income',
    name     : FieldNames.otherIncome,
    showIf   : [isEmploymentOther],
  },
  {
    id       : 1057,
    component: BankAccounts,
    options  : ACCOUNT_TYPE_REFINANCE_OPTIONS,
    name     : FieldNames.assets,
  },
  {
    id        : 1058,
    component : TextInput,
    label     : 'Job Title',
    isOptional: true,
    name      : FieldNames.employTitle,
    showIf    : [isEmployee, isSelfEmployed],
  },
  {
    id         : 1059,
    component  : Year,
    label      : 'Year Built',
    name       : FieldNames.builtYear,
    isOptional : true,
  },
  {
    id         : 1060,
    component  : Year,
    label      : 'Year Acquired',
    name       : FieldNames.acquiredYear,
    isOptional : true,
  },
  {
    id         : 1061,
    component  : Currency,
    label      : 'Original Cost',
    name       : FieldNames.propertyOriginalCost,
    isOptional : true,
  },
  {
    ...SHARED[FieldNames.propertyType],
    id           : 1062,
    component    : Select,
    fieldPopover : true,
    name         : FieldNames.livingPropertyType,
    showIf       : [isSecondaryHome, isInvestmentHome],
  },
  {
    id        : 1063,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.livingPropertyStreet,
    showIf    : [isSecondaryHome, isInvestmentHome],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet,
    },
  },
  {
    id         : 1064,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.livingPropertyStreet2,
    showIf     : [isSecondaryHome, isInvestmentHome],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPropertyStreet2,
    },
  },
  {
    id        : 1065,
    component : Address,
    label     : 'City',
    name      : FieldNames.livingPropertyCity,
    showIf    : [isSecondaryHome, isInvestmentHome],
  },
  {
    id        : 1066,
    component : State,
    label     : 'State',
    name      : FieldNames.livingPropertyState,
    showIf    : [isSecondaryHome, isInvestmentHome],
  },
  {
    ...SHARED[FieldNames.propertyZip],
    id     : 1067,
    name   : FieldNames.livingPropertyZip,
    showIf : [isSecondaryHome, isInvestmentHome],
  },
  {
    id        : 1070,
    component : SinglePastDate,
    label     : 'Occupancy Start Date',
    name      : FieldNames.livingOccupancyStart,
  },
  {
    id        : 1071,
    title     : 'Previous Address',
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.livingPrevPropertyStreet,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPrevPropertyStreet,
    },
  },
  {
    id         : 1072,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.livingPrevPropertyStreet2,
    showIf     : [isLessThanTwoYrsAtLivingAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.livingPrevPropertyStreet2,
    },
  },
  {
    id        : 1073,
    component : Address,
    label     : 'City',
    name      : FieldNames.livingPrevPropertyCity,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    id        : 1074,
    component : State,
    label     : 'State',
    name      : FieldNames.livingPrevPropertyState,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    ...SHARED[FieldNames.propertyZip],
    id     : 1075,
    name   : FieldNames.livingPrevPropertyZip,
    showIf : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    id        : 1076,
    component : Select,
    label     : 'Housing Situation',
    name      : FieldNames.livingPrevRentOrOwn,
    options   : LIVING_SITUATION_OPTIONS,
    showIf    : [isLessThanTwoYrsAtLivingAddress],
  },
  {
    id       : 1132,
    component: SinglePastDate,
    label    : 'Occupancy Start Date',
    name     : FieldNames.livingPrevOccupancyStart,
    showIf   : [isLessThanTwoYrsAtLivingAddress],
    validator: {
      type : 'isBeforeEndDate',
      param: FieldNames.livingOccupancyStart,
    },
  },
  {
    id        : 1077,
    component : Currency,
    label     : 'Annual Taxes',
    name      : FieldNames.yearTaxes,
  },
  {
    id        : 1078,
    component : Currency,
    label     : 'Annual Insurance',
    name      : FieldNames.yearInsure,
  },
  {
    id         : 1079,
    component  : Currency,
    isOptional : true,
    label      : 'Annual Homeowners Association Fees',
    name       : FieldNames.yearAssociate,
  },
  {
    ...SHARED[FieldNames.email],
    id     : 1080,
    name   : FieldNames.coBorrowerEmail,
    showIf : [hasCoBorrower],
  },
  {
    ...SHARED[FieldNames.phone],
    id     : 1081,
    name   : FieldNames.coBorrowerPhone,
    showIf : [hasCoBorrower],
  },
  {
    id        : 1082,
    component : RadioField,
    name      : FieldNames.coBorrowerSameAddress,
    options   : YES_OR_NO_OPTIONS,
    title     : 'Same address as borrower?',
    showIf    : [hasCoBorrower],
  },
  {
    id        : 1083,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.coBorrowerLivingPropertyStreet,
    title     : `Co-borrower's current address?`,
    showIf    : [coBorrowerHasDifferentAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.coBorrowerLivingPropertyStreet,
    },
  },
  {
    id         : 1084,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.coBorrowerLivingPropertyStreet2,
    showIf     : [coBorrowerHasDifferentAddress],
    validator: {
      type : 'isMailingAddress',
      param: FieldNames.coBorrowerLivingPropertyStreet2,
    },
  },
  {
    id        : 1085,
    component : Address,
    label     : 'City',
    name      : FieldNames.coBorrowerLivingPropertyCity,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    id        : 1086,
    component : State,
    label     : 'State',
    name      : FieldNames.coBorrowerLivingPropertyState,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    id        : 1087,
    component : ZipCode,
    label     : 'Zip Code',
    name      : FieldNames.coBorrowerLivingPropertyZip,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    id        : 1088,
    component : RadioField,
    name      : FieldNames.coBorrowerLivingRentOrOwn,
    options   : LIVING_SITUATION_OPTIONS,
    title     : `Co-borrower's current housing situation?`,
    showIf    : [coBorrowerHasDifferentAddress],
  },
  {
    id        : 1089,
    component : Currency,
    label     : 'Monthly Rent Payment',
    name      : FieldNames.coBorrowerLivingMonthlyRentAmount,
    showIf    : [isCoBorrowerRenting],
  },
  {
    ...SHARED[FieldNames.maritalStatus],
    id     : 1090,
    name   : FieldNames.coBorrowerMaritalStatus,
    title  : `Co-borrower's marital status?`,
    showIf : [hasCoBorrower],
  },
  {
    ...SHARED[FieldNames.phone],
    id   : 1091,
    name : FieldNames.employPhone,
  },
  {
    id        : 1092,
    component : RadioField,
    name      : FieldNames.coBorrowerEmploymentType,
    options   : COBORROWER_EMPLOYMENT_TYPE_OPTIONS,
  },
  {
    id        : 1093,
    component : TextInput,
    label     : 'Company Name',
    name      : FieldNames.coBorrowerEmployCompanyName,
  },
  {
    id        : 1094,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.coBorrowerEmployStreet,
  },
  {
    id         : 1095,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.coBorrowerEmployStreet2,
  },
  {
    id        : 1096,
    component : Address,
    label     : 'City',
    name      : FieldNames.coBorrowerEmployCity,
  },
  {
    id        : 1097,
    component : State,
    label     : 'State',
    name      : FieldNames.coBorrowerEmployState,
  },
  {
    id        : 1098,
    component : ZipCode,
    label     : 'Zip Code',
    name      : FieldNames.coBorrowerEmployZip,
  },
  {
    id         : 1099,
    component  : TextInput,
    label      : 'Job Title',
    isOptional : true,
    name       : FieldNames.coBorrowerEmployTitle,
  },
  {
    ...SHARED[FieldNames.phone],
    id   : 1100,
    name : FieldNames.coBorrowerEmployPhone,
  },
  {
    id        : 1101,
    component : SinglePastDate,
    label     : 'Start Date',
    name      : FieldNames.coBorrowerEmployStart,
  },
  {
    id        : 1102,
    component : Currency,
    label     : 'Monthly Base Pay',
    name      : FieldNames.coBorrowerEmployBase,
  },
  {
    id           : 1103,
    component    : Currency,
    fieldPopover : true,
    isOptional   : true,
    label        : 'Monthly Overtime',
    name         : FieldNames.coBorrowerEmployOvertime,
    title        : 'Additional income sources?',
    popover      : 'Overtime can be included in your income if it has been consistent over a two-year period.',
  },
  {
    id           : 1104,
    component    : Currency,
    fieldPopover : true,
    isOptional   : true,
    label        : 'Monthly Commission',
    name         : FieldNames.coBorrowerEmployCommission,
    popover      : 'Commissions can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id           : 1105,
    component    : Currency,
    fieldPopover : true,
    isOptional   : true,
    label        : 'Monthly Bonus',
    name         : FieldNames.coBorrowerEmployBonus,
    popover      : 'Bonuses can be included in your income if they have been consistent over a two-year period.',
  },
  {
    id        : 1106,
    component : IncomeSources,
    name      : FieldNames.coBorrowerAdditionalIncome,
  },
  {
    id        : 1107,
    component : TextInput,
    label     : 'Previous Employers Name',
    name      : FieldNames.coBorrowerEmployPrevName,
    title     : 'Previous employment information',
    showIf    : [isCoLessThanTwoYrsAtJob],
    popover   : `Lenders will require previous employer information if you've been with this employer for less than 2 years.`,
  },
  {
    id        : 1108,
    component : Address,
    label     : 'Street Address',
    name      : FieldNames.coBorrowerEmployPrevStreet,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id         : 1109,
    component  : Address,
    isOptional : true,
    label      : 'Street Address 2',
    name       : FieldNames.coBorrowerEmployPrevStreet2,
    showIf     : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1110,
    component : Address,
    label     : 'City',
    name      : FieldNames.coBorrowerEmployPrevCity,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1111,
    component : State,
    label     : 'State',
    name      : FieldNames.coBorrowerEmployPrevState,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1112,
    component : ZipCode,
    label     : 'Zip',
    name      : FieldNames.coBorrowerEmployPrevZip,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1113,
    component : SinglePastDate,
    label     : 'Start Date',
    name      : FieldNames.coBorrowerEmployPrevStart,
    showIf    : [isCoLessThanTwoYrsAtJob],
  },
  {
    id        : 1114,
    component : SinglePastDate,
    label     : 'End Date',
    name      : FieldNames.coBorrowerEmployPrevEnd,
    showIf    : [isCoLessThanTwoYrsAtJob],
    validator: {
      type : 'isAfterStartDate',
      param: [FieldNames.coBorrowerEmployPrevStart, FieldNames.coBorrowerEmployStart],
    },
  },
  {
    id        : 1115,
    component : RetirementIncomeSources,
    title     : `Tell us about your co-borrower's retirement income`,
    name      : FieldNames.coBorrowerRetirementIncome,
    showIf    : [isCoBorrowerRetired],
  },
  {
    id        : 1116,
    component : OtherIncomeSources,
    title     : `Tell us about your co-borrower's other income`,
    name      : FieldNames.coBorrowerOtherIncome,
    showIf    : [isCoBorrowerEmploymentOther],
  },
  {
    id: 1119,
    ...SHARED[FieldNames.suffixName],
  },
  {
    id: 1120,
    ...SHARED[FieldNames.coBorrowerSuffixName],
  },
  {
    id: 1121,
    ...SHARED[FieldNames.employPrevTitle],
  },
  {
    id: 1122,
    ...SHARED[FieldNames.coBorrowerEmployPrevTitle],
  },
  {
    id: 1123,
    ...SHARED[FieldNames.citizenshipType],
  },
  {
    id: 1124,
    ...SHARED[FieldNames.coBorrowerCitizenshipType],
  },
  {
    id: 1125,
    ...SHARED[FieldNames.militaryExperience],
  },
  {
    id: 1126,
    ...SHARED[FieldNames.coBorrowerMilitaryExperience],
  },
  {
    id: 1127,
    ...SHARED[FieldNames.employOwnershipShare],
  },
  {
    id: 1128,
    ...SHARED[FieldNames.coBorrowerEmployOwnershipShare],
  },
  {
    id: 1129,
    ...SHARED[FieldNames.employPartyTransaction],
  },
  {
    id: 1130,
    ...SHARED[FieldNames.coBorrowerEmployPartyTransaction],
  },
  {
    ...SHARED[FieldNames.propertyMonthlyRentalIncome],
    id     : 1131,
    label  : 'Current Monthly Rental Income',
  },
  {
    id        : 1132,
    component : CollectConditionallyRequiredSsn,
    name      : FieldNames.ssnQuestionRequirement,
  },
  {
    id       : 1133,
    component: RadioField,
    name     : FieldNames.isMailingDifferent,
    options  : YES_OR_NO_OPTIONS,
    showIf   : [isCollectingMailingAddress],
    title    : 'Is your mailing address different?',
  },
  {
    id        : 1134,
    component : Address,
    isOptional: false,
    label     : 'Street Address',
    name      : FieldNames.mailingStreet,
    showIf: [isMailingDifferent],
  },
  {
    id        : 1135,
    component : Address,
    isOptional: true,
    label     : 'Street Address 2',
    name      : FieldNames.mailingStreet2,
    showIf: [isMailingDifferent],

  },
  {
    id       : 1136,
    component: Address,
    label    : 'City',
    name     : FieldNames.mailingCity,
    showIf   : [isMailingDifferent],
  },
  {
    id       : 1137,
    component: State,
    label    : 'State',
    name     : FieldNames.mailingState,
    showIf   : [isMailingDifferent],
  },
  {
    id       : 1138,
    component: ZipCode,
    label    : 'Zip Code',
    name     : FieldNames.mailingZip,
    showIf   : [isMailingDifferent],
  },
  {
    id: 1139,
    component: RadioField,
    name: FieldNames.loanType,
    options: LoanTypeOptions,
    title: 'What type of loan would you like?',
  },
  {
    id       : 1140,
    component: IntegerAllowBlank,
    name     : FieldNames.lineOfWorkYears,
    label    : 'Years',
    isOptional : true,
    title    : 'How long have you been in this line of work?',
    showIf   : [hasLineOfWorkQuestion],
    validator: {
      type : 'isLineOfWorkYears',
      param: FieldNames.lineOfWorkYears,
    },
  },
  {
    id       : 1141,
    component: IntegerAllowBlank,
    name     : FieldNames.lineOfWorkMonths,
    label    : 'Months',
    isOptional : true,
    showIf   : [hasLineOfWorkQuestion],
    validator: {
      type : 'isLineOfWorkMonths',
      param: FieldNames.lineOfWorkMonths,
    },
  },
  {
    id       : 1142,
    component: IntegerAllowBlank,
    name     : FieldNames.coBorrowerLineOfWorkYears,
    title    : 'How long have they been in this line of work?',
    label    : 'Years',
    isOptional : true,
    showIf   : [hasLineOfWorkQuestion],
    validator: {
      type : 'isLineOfWorkYears',
      param: FieldNames.lineOfWorkYears,
    },
  },
  {
    id       : 1143,
    component: IntegerAllowBlank,
    name     : FieldNames.coBorrowerLineOfWorkMonths,
    label    : 'Months',
    isOptional : true,
    showIf   : [hasLineOfWorkQuestion],
    validator: {
      type : 'isLineOfWorkMonths',
      param: FieldNames.lineOfWorkMonths,
    },
  },

];
